import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { axiosClient } from '../lib/api.ts';
import { useStore } from '../lib/store.ts';

export interface StravaJob {
  id: number;
  status: 'running' | 'done' | 'failed';
  started_at: string | null;
  finished_at: string | null;
  last_sync_at: string | null;
  activities_fetched: number;
  activities_inserted: number;
  activities_updated: number;
  error_text: string | null;
}

export interface StravaStatus {
  configured: boolean;
  connected: boolean;
  athlete_id?: number;
  connected_at?: string;
  scope?: string | null;
  last_job: StravaJob | null;
}

const STATUS_QUERY_KEY = ['strava', 'status'] as const;

/**
 * Strava integration state + actions for the Profile page.
 *
 * - ``status``: current connection state, polled every 3s while a sync
 *   job is running so the UI shows live progress.
 * - ``connect``: redirects the browser to Strava's authorize URL. The
 *   browser navigation drops the SPA's auth header — that's expected;
 *   the ``state`` JWT in the URL carries user_id back to /strava/callback.
 * - ``sync``: kicks off a manual backfill / incremental sync.
 * - ``disconnect``: revokes the token at Strava and deletes our row.
 */
export function useStrava() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addToast = useStore((s) => s.addToast);

  const status = useQuery<StravaStatus>({
    queryKey: STATUS_QUERY_KEY,
    queryFn: () => axiosClient.get<StravaStatus>('/strava/status').then((r) => r.data),
    // Poll every 3s while the importer is running; stop polling otherwise.
    refetchInterval: (query) => {
      const last = query.state.data?.last_job;
      return last?.status === 'running' ? 3000 : false;
    },
    // Survive tab-switch without an immediate refetch — sync is server-side.
    staleTime: 5_000,
  });

  const connect = useMutation({
    mutationFn: () =>
      axiosClient.get<{ authorize_url: string }>('/strava/connect').then((r) => r.data),
    onSuccess: ({ authorize_url }) => {
      // Full page navigation — Strava's authorize page needs the user's
      // browser, not an XHR. After approval Strava redirects to
      // /strava/callback which renders the success page; user navigates
      // back to the SPA manually (or we can deep-link from there later).
      globalThis.location.href = authorize_url;
    },
    onError: () => {
      addToast({ message: t('strava.errors.connectFailed'), type: 'error' });
    },
  });

  const sync = useMutation({
    mutationFn: () =>
      axiosClient.post<{ status: string }>('/strava/sync').then((r) => r.data),
    onSuccess: () => {
      addToast({ message: t('strava.toasts.syncStarted'), type: 'success' });
      // Force a status refetch so the UI shows running immediately.
      queryClient.invalidateQueries({ queryKey: STATUS_QUERY_KEY });
    },
    onError: () => {
      addToast({ message: t('strava.errors.syncFailed'), type: 'error' });
    },
  });

  // When a backfill transitions running -> done, refetch any ride-shaped
  // queries so new activities pop into the calendar / lists without a
  // manual reload. Track the prior status with a ref instead of state so
  // we don't kick another render cycle when polling fires.
  const priorJobStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const current = status.data?.last_job?.status ?? null;
    if (priorJobStatusRef.current === 'running' && current === 'done') {
      queryClient.invalidateQueries({ queryKey: ['rides'] });
    }
    priorJobStatusRef.current = current;
  }, [status.data?.last_job?.status, queryClient]);

  const disconnect = useMutation({
    mutationFn: () =>
      axiosClient.post<{ status: string }>('/strava/disconnect').then((r) => r.data),
    onSuccess: () => {
      addToast({ message: t('strava.toasts.disconnected'), type: 'success' });
      queryClient.invalidateQueries({ queryKey: STATUS_QUERY_KEY });
    },
    onError: () => {
      addToast({ message: t('strava.errors.disconnectFailed'), type: 'error' });
    },
  });

  return { status, connect, sync, disconnect };
}
